import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

import { useParams } from "react-router-dom";

import {
  api,
  clearPosSession,
  getPosHeaders,
  getPosSession,
} from "./api";

const PermissionContext = createContext(null);

export function PermissionProvider({ children }) {
  const { company } = useParams();

  const [state, setState] = useState({
    permissions: [],
    user: null,
    loadingPermissions: true,
    error: "",
  });

  const refreshPermissions = useCallback(async () => {
    const session = getPosSession(company);

    if (!session?.access_token) {
      setState({
        permissions: [],
        user: null,
        loadingPermissions: false,
        error: "",
      });
      return;
    }

    const token = session.access_token;

    setState({
      permissions: [],
      user: null,
      loadingPermissions: true,
      error: "",
    });

    try {
      const headers = getPosHeaders(company);

      const [userResponse, permissionResponse] = await Promise.all([
        api.get("/auth/pos/me", { headers }),
        api.get("/auth/pos/permissions", { headers }),
      ]);

      // Ignorar respuestas de una sesión que ya fue reemplazada.
      if (getPosSession(company)?.access_token !== token) return;

      if (
        userResponse.data.subdomain !== company ||
        userResponse.data.company_id !== permissionResponse.data.company_id
      ) {
        clearPosSession(company);

        setState({
          permissions: [],
          user: null,
          loadingPermissions: false,
          error: "",
        });
        return;
      }

      setState({
        permissions: permissionResponse.data.permissions || [],
        user: userResponse.data,
        loadingPermissions: false,
        error: "",
      });
    } catch (error) {
      if (getPosSession(company)?.access_token !== token) return;

      const status = error.response?.status;

      if (status === 401 || status === 403) {
        clearPosSession(company);

        setState({
          permissions: [],
          user: null,
          loadingPermissions: false,
          error: "",
        });
        return;
      }

      setState({
        permissions: [],
        user: null,
        loadingPermissions: false,
        error: "No fue posible comprobar la sesión. Revisa el backend.",
      });
    }
  }, [company]);

  useEffect(() => {
    refreshPermissions();
  }, [refreshPermissions]);

  const value = useMemo(
    () => ({
      ...state,
      role: state.user?.role || "",
      refreshPermissions,
      can: (code) => state.permissions.includes(code),
    }),
    [state, refreshPermissions]
  );

  return (
    <PermissionContext.Provider value={value}>
      {children}
    </PermissionContext.Provider>
  );
}

export function usePermissions() {
  const context = useContext(PermissionContext);

  if (!context) {
    throw new Error(
      "usePermissions debe usarse dentro de PermissionProvider."
    );
  }

  return context;
}